import { useContext } from 'react';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import {
  applicationKeys,
  parseApplicationKey,
} from '../cache-keys/applicationKeys';
import { loan_quote, loan_request } from '@axo/shared/data-access/types';
import { loan_request_api } from '@axo/shared/data-access/api';
import { loan_quote_api } from '@axo/shared/data-access/api';
import { DataAccessContext, useAPI } from '@axo/shared/data-access/provider';
import { queryFunction } from '../query-function';
import { LoanQuoteRequest } from '@axo/shared/data-access/types/loan_quote';

export function useLoanQuotes(applicationID: string | undefined) {
  const { state } = useContext(DataAccessContext);
  const cacheKey = applicationKeys.loanQuotes({
    applicationID,
  });
  const api = useAPI();
  return useQuery(
    cacheKey,
    queryFunction(loan_request_api.find(api), ({ queryKey }) => ({
      loanApplicationID: parseApplicationKey(queryKey).applicationID ?? '',
    })),
    {
      enabled: state.user.token.length > 0 && !!applicationID,
      select: getQuotesByBanks,
    }
  );
}

export function useCreateLoanQuote() {
  const api = useAPI();
  const client = useQueryClient();

  return useMutation(
    ({ applicationID, data }: LoanQuoteRequest) =>
      loan_quote_api.createLoanQuote(api)({
        applicationID,
        data,
      }),
    {
      onSuccess: (_, params) => {
        return client.invalidateQueries(
          applicationKeys.loanQuotes({
            applicationID: params.applicationID,
          })
        );
      },
    }
  );
}

export function usePatchLoanQuote() {
  const api = useAPI();
  const client = useQueryClient();

  return useMutation(
    ({ applicationID, loanQuoteID, data }: LoanQuoteRequest) =>
      loan_quote_api.patchLoanQuote(api)({
        applicationID,
        loanQuoteID,
        data,
      }),
    {
      onSuccess: (_, params) => {
        return client.invalidateQueries(
          applicationKeys.loanQuotes({
            applicationID: params.applicationID,
          })
        );
      },
    }
  );
}

export function useLoanQuotesForMultipleApplication(applicationIDs: string[]) {
  const { state } = useContext(DataAccessContext);
  const api = useAPI();
  const queries = applicationIDs.map<
    UseQueryOptions<
      loan_request.LoanRequest[],
      unknown,
      loan_request.LoanRequest[],
      ReturnType<typeof applicationKeys.loanQuotes>
    >
  >((appID) => ({
    queryKey: applicationKeys.loanQuotes({
      applicationID: appID,
    }),
    queryFn: queryFunction(loan_request_api.find(api), ({ queryKey }) => ({
      loanApplicationID: parseApplicationKey(queryKey).applicationID ?? '',
    })),
    enabled: state.user.token.length > 0 && !!appID,
  }));
  return useQueries(queries);
}

function getLoanRequestByBanks(loanRequests: loan_request.LoanRequest[]) {
  let banks: { [index: string]: loan_quote.BankRequest } = {};

  loanRequests.forEach((loanRequest) => {
    if (banks[loanRequest.LenderID]) {
      banks = {
        ...banks,
        [loanRequest.LenderID]: {
          ...banks[loanRequest.LenderID],
          LoanRequests: [
            ...banks[loanRequest.LenderID].LoanRequests,
            loanRequest,
          ],
        },
      };
    } else {
      banks[loanRequest.LenderID] = {
        LenderID: loanRequest.LenderID,
        LoanRequests: [loanRequest],
      };
    }
  });
  return Object.values(banks);
}

const compareCreatedDates = (
  a: loan_request.LoanRequest,
  b: loan_request.LoanRequest
) => {
  return new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime();
};

function getQuotesByBanks(
  loanRequests: loan_request.LoanRequest[]
): loan_quote.BankQuote[] {
  const quotes = getLoanRequestByBanks(loanRequests)
    .sort((a, b) => a.LenderID.localeCompare(b.LenderID))
    .map((bank) =>
      bank.LoanRequests.sort(compareCreatedDates).map((request) => {
        if (
          request.LoanQuotes === undefined ||
          (request.LoanQuotes?.length || 0) < 1
        ) {
          const loanQuote: loan_quote.BankQuote = {
            ...request,
            // @ts-expect-error type mismatch
            Status: undefined,
            LoanRequestID: request.ID,
            LenderID: bank.LenderID,
            EffectiveRate: null,
            NominalRate: null,
            MonthlyPayment: 0,
            StartupFee: 0,
            AdministrationFee: 0,
            Amount: 0,
            Duration: 0,
            TotalPayment: 0,
            ApplicationID: request.LoanApplicationID,
            IsPlaceholderQuote: true,
            LoanRequest: {
              LoanRequestID: request.ID,
              CreatedAt: request.CreatedAt,
              CreatedBy: request.CreatedBy,
              Status: request.Status,
              LenderStatus: request.LenderStatus,
              Amount: request.Amount,
              Duration: request.Duration,
              LenderReference: request.LenderReference,
            },
          };
          return loanQuote;
        } else
          return request.LoanQuotes.map((quote) => {
            const loanQuote: loan_quote.BankQuote = {
              ...quote,
              LenderID: bank.LenderID,
              ApplicationID: request.LoanApplicationID,
              LoanRequest: {
                LoanRequestID: request.ID,
                CreatedAt: request.CreatedAt,
                CreatedBy: request.CreatedBy,
                Status: request.Status,
                LenderStatus: request.LenderStatus,
                Amount: request.Amount,
                Duration: request.Duration,
                LenderReference: request.LenderReference,
              },
            };
            return loanQuote;
          });
      })
    );

  return quotes.flat().flat();
}
